import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDelete } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { isUnsupportedEnrollmentType } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { FIELD_CONFIGS } from '@backrs_ui/grow-ui/src/pages/Profile/configs/field.config';
import { ProfileFieldConfig } from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import {
  handleFieldChange,
  handleUpdatingAnswer,
} from '@backrs_ui/grow-ui/src/pages/Profile/utils/fields.helpers';
import { filterEditProfileProfileOptions } from '@backrs_ui/grow-ui/src/pages/Profile/utils/options.helpers';
import {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import {
  Box,
  Typography,
  SxProps,
  Theme,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

const ASSESSMENT_CHECKLIST_STRINGS =
  appStrings.modules.assessMyTrack.assessmentChecklistAcademicType;

export const AssessmentChecklistAcademicType = (): ReactElement => {
  const navigate = useNavigate();
  const [updateProfile] = useUpdateUserProfileMutation();
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const fieldKey = 'academicEnrollmentTypes';

  const { refetch } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  const config = FIELD_CONFIGS[fieldKey] as ProfileFieldConfig;
  const { data: unfilteredProfileOptions } = useFetchProfileValueOptionsQuery(undefined);
  const profileOptions = filterEditProfileProfileOptions(unfilteredProfileOptions);
  const options = profileOptions?.[fieldKey];

  const radioOptions =
    options?.map((option) => ({
      label: typeof option === 'string' ? option : option.name,
      value: String(typeof option === 'string' ? option : option.id),
      level: typeof option === 'string' ? option : option.level,
      code: typeof option === 'string' ? option : option.code,
    })) || [];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const saveEnrollment = () => {
    handleFieldChange({
      config,
      values: [selectedValue],
      fieldKey,
      handleUpdatingAnswer: (key, value) => handleUpdatingAnswer(updateProfile, key, value),
      refetchUserProfile: refetch,
    });
  };

  const handleSave = () => {
    const selected = radioOptions.find((o) => o.value === selectedValue);

    if (selected && isUnsupportedEnrollmentType({ level: selected.level, code: selected.code })) {
      setShowConfirmDialog(true);

      return;
    }

    saveEnrollment();
  };

  const handleConfirmUnsupported = async () => {
    setShowConfirmDialog(false);
    await updateProfile({ field: fieldKey, value: [selectedValue] });
    refetch();
    navigate(APP_ROUTE.HOME);
  };

  return (
    <Box sx={containerSx}>
      <Box sx={subContainerSx}>
        <Box sx={titleContainerSx}>
          <Typography variant="text-lg-semibold">{ASSESSMENT_CHECKLIST_STRINGS.title}</Typography>
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {ASSESSMENT_CHECKLIST_STRINGS.description}
          </Typography>
        </Box>

        <Box sx={contentSx}>
          {radioOptions.length > 0 && (
            <Box sx={radioContainerSx}>
              <RadioGroup
                value={selectedValue}
                onChange={handleChange}
                id="assess-track-academic-type-radio-group"
              >
                {radioOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    id={`assess-track-academic-type-option-${option.level}`}
                    control={<Radio id={`assess-track-academic-type-radio-${option.level}`} />}
                    label={<Typography variant="text-sm-medium">{option.label}</Typography>}
                    checked={option.value === selectedValue}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Box>

        {radioOptions.length > 0 && (
          <Button
            hierarchy="Primary"
            size="md"
            onClick={handleSave}
            sx={buttonSx}
            id="assess-track-academic-type-save-button"
          >
            {ASSESSMENT_CHECKLIST_STRINGS.save}
          </Button>
        )}
      </Box>
      <ConfirmDelete
        openDialog={showConfirmDialog}
        setOpenDialog={setShowConfirmDialog}
        variant="warning"
        headerText={ASSESSMENT_CHECKLIST_STRINGS.unsupportedEnrollmentConfirmTitle}
        bodyText={ASSESSMENT_CHECKLIST_STRINGS.unsupportedEnrollmentConfirmBody}
        confirmButtonText={ASSESSMENT_CHECKLIST_STRINGS.unsupportedEnrollmentConfirmButton}
        handleDelete={handleConfirmUnsupported}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  maxWidth: '100%',
  minWidth: '100%',
  '@container (min-width: 900px)': {
    maxWidth: '350px',
    minWidth: '350px',
  },
  '@container (min-width: 1200px)': {
    maxWidth: '400px',
    minWidth: '400px',
  },
  height: '100%',
});

const subContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-lg'],
  padding: backrs.spacing['spacing-xl'],
  '@container (min-width: 600px)': {
    padding: backrs.spacing['spacing-3xl'],
  },
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const buttonSx: SxProps<Theme> = {
  width: '100%',
};

const contentSx: SxProps<Theme> = {
  flex: 1,
};

const radioContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-2xl'],
  marginBottom: backrs.spacing['spacing-2xl'],
});
